import {
  AccountingArchiveDocumentType,
  ArchiveDocumentPage,
  InvoiceDocument,
  OutstandingInvoicesForCompany,
  OutstandingPaymentsForCompany,
  OutstandingSettlementsForCompany,
  Payment,
  Transaction,
} from "@/types";
import { axios, APIBASE_REVIVERACCOUNTING } from "./ApiConfig";
import { useQueries, useQuery, UseQueryOptions } from "@tanstack/react-query";

export const getTransactionInvoices = async (transactionId: any) =>
  axios.get(`${APIBASE_REVIVERACCOUNTING}api/accounting/invoice/transaction/${transactionId}`);

export const useGetTransactionInvoices = (transactionId: any, options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery<InvoiceDocument[] | undefined>({
    queryKey: ["getTransactionInvoices", transactionId],
    queryFn: async () => {
      const result = await getTransactionInvoices(transactionId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const useGetOrderTransactionsInvoices = (transactions: Transaction[], options: any) => {
  return useQueries({
    queries: transactions.map((trans) => ({
      queryKey: ["getTransactionInvoices", trans.id],
      queryFn: async (): Promise<InvoiceDocument[]> => {
        const result = await getTransactionInvoices(trans.id);
        return result.data;
      },
      enabled: !!trans.id,
      refetchOnWindowFocus: false,
      ...options,
    })),
    combine: (results) => {
      return results
        .filter((result) => result.isSuccess && result.data && result.data)
        .flatMap((result) => result.data as InvoiceDocument[]);
    },
  });
};

export const getPaymentsForCompany = async (
  companyId: number,
  limit: number,
  onlyUnpaid: boolean,
  sortDirection: "DESC" | "ASC"
) =>
  axios.get(`${APIBASE_REVIVERACCOUNTING}api/accounting/payment/company/${companyId}`, {
    params: {
      limit: limit,
      onlyUnpaid: onlyUnpaid,
      sortDirection: sortDirection,
    },
  });

export const useGetPaymentsForCompany = (
  companyId: number,
  limit: number,
  onlyUnpaid: boolean,
  sortDirection: "DESC" | "ASC",
  options?: any
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<Payment[]>({
    queryKey: ["getPaymentsForCompany", companyId, limit, onlyUnpaid, sortDirection],
    queryFn: async () => {
      const result = await getPaymentsForCompany(companyId, limit, onlyUnpaid, sortDirection);
      return result.data;
    },
    ...queryOptions,
  });
};

const getInvoicesForCompanyId = async (
  companyId: number,
  limit: number,
  onlyUnpaid: boolean,
  isErpInvoiceCreated: boolean,
  sortDirection: "DESC" | "ASC"
) =>
  axios.get(`${APIBASE_REVIVERACCOUNTING}api/accounting/invoice/company/${companyId}`, {
    params: {
      limit: limit,
      onlyUnpaid: onlyUnpaid,
      isErpInvoiceCreated: isErpInvoiceCreated,
      sortDirection: sortDirection,
    },
  });

export const useGetInvoicesForCompanyId = (
  companyId: number,
  limit: number,
  onlyUnpaid: boolean,
  isErpInvoiceCreated: boolean,
  sortDirection: "DESC" | "ASC",
  options?: any
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<InvoiceDocument[]>({
    queryKey: ["getInvoicesForCompanyId", companyId, limit, onlyUnpaid, isErpInvoiceCreated, sortDirection],
    queryFn: async () => {
      const result = await getInvoicesForCompanyId(companyId, limit, onlyUnpaid, isErpInvoiceCreated, sortDirection);
      return result.data;
    },
    ...queryOptions,
  });
};

const getDeviationInvoices = async (buyOrderId: number) =>
  axios.get<string[]>(`${APIBASE_REVIVERACCOUNTING}api/accounting/simpleInvoice/${buyOrderId}/document`);

export const useGetDeviationInvoices = (buyOrderId: number, options?: Partial<UseQueryOptions<string[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<string[]>({
    queryKey: ["getDeviationInvoice", buyOrderId],
    queryFn: async () => {
      const result = await getDeviationInvoices(buyOrderId);
      return result.data;
    },
    ...queryOptions,
  });
};

const getDocumentByCompanyIdAndType = async (
  companyId: number,
  page: number,
  pageSize: number,
  sortDirection: "DESC" | "ASC",
  documentType: AccountingArchiveDocumentType
) =>
  axios.get<ArchiveDocumentPage>(`${APIBASE_REVIVERACCOUNTING}api/document/company/${companyId}`, {
    params: {
      page: page,
      pageSize: pageSize,
      sortDirection: sortDirection,
      documentType: documentType,
    },
  });

export const useGetDocumentByCompanyIdAndType = (
  companyId: number,
  page: number,
  pageSize: number,
  sortDirection: "DESC" | "ASC",
  documentType: AccountingArchiveDocumentType,
  options?: Partial<UseQueryOptions<ArchiveDocumentPage>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<ArchiveDocumentPage>({
    queryKey: ["getDocumentByCompanyIdAndType", companyId, page, pageSize, sortDirection, documentType],
    queryFn: async () => {
      const result = await getDocumentByCompanyIdAndType(companyId, page, pageSize, sortDirection, documentType);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getDocumentById = async (invoiceId: number, documentType: AccountingArchiveDocumentType) =>
  axios.get<ArrayBuffer>(`${APIBASE_REVIVERACCOUNTING}api/document/${invoiceId}`, {
    params: {
      documentType: documentType,
    },
    responseType: "arraybuffer",
  });

export const useGetDocumentById = (
  invoiceId: number,
  documentType: AccountingArchiveDocumentType,
  options?: Partial<UseQueryOptions<ArrayBuffer>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<ArrayBuffer>({
    queryKey: ["getDocumentById", invoiceId, documentType],
    queryFn: async () => {
      const result = await getDocumentById(invoiceId, documentType);
      return result.data;
    },
    ...queryOptions,
  });
};

const getOutstandingPaymentsForCompany = async (companyId: number) =>
  axios.get<OutstandingPaymentsForCompany>(
    `${APIBASE_REVIVERACCOUNTING}api/accounting/outstanding/payments/${companyId}`
  );

export const useGetOutstandingPaymentsForCompany = (
  companyId: number,
  options?: Partial<UseQueryOptions<OutstandingPaymentsForCompany>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<OutstandingPaymentsForCompany>({
    queryKey: ["getOutstandingPaymentsForCompany", companyId],
    queryFn: async () => {
      const result = await getOutstandingPaymentsForCompany(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

const getOutstandingInvoicesForCompany = async (companyId: number) =>
  axios.get<OutstandingInvoicesForCompany>(
    `${APIBASE_REVIVERACCOUNTING}api/accounting/outstanding/invoices/${companyId}`
  );

export const useGetOutstandingInvoicesForCompany = (
  companyId: number,
  options?: Partial<UseQueryOptions<OutstandingInvoicesForCompany>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<OutstandingInvoicesForCompany>({
    queryKey: ["getOutstandingInvoicesForCompany", companyId],
    queryFn: async () => {
      const result = await getOutstandingInvoicesForCompany(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

const getOutstandingSettlementsForCompany = async (companyId: number) =>
  axios.get<OutstandingSettlementsForCompany>(
    `${APIBASE_REVIVERACCOUNTING}api/accounting/outstanding/settlements/${companyId}`
  );

export const useGetOutstandingSettlementsForCompany = (
  companyId: number,
  options?: Partial<UseQueryOptions<OutstandingSettlementsForCompany>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<OutstandingSettlementsForCompany>({
    queryKey: ["getOutstandingSettlementsForCompany", companyId],
    queryFn: async () => {
      const result = await getOutstandingSettlementsForCompany(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};
